<template>
	<kanban-card :item="item" :edge-state="stateKey">
		<template #default>
			<v-card-text class="pb-3">
				<div class="text-subtitle-2 font-weight-medium mb-2">
					{{ item.paperName || "Untitled Paper" }}
				</div>

				<!-- Status flavor inside the merged "My Papers" column: DCTL-2257 gives
				every state in it a chip (not-started, in progress, rework), each with
				the mandatory per-state glyph, since the column holds three states and
				hue alone cannot tell them apart. DCTL-2195: a reassigned extraction
				also shows a "From {name}" provenance chip, so the two flow together in
				one wrapping row with a consistent gap. -->
				<div
					v-if="statusChip || item.originalAssignee"
					class="d-flex flex-wrap ga-1 mb-2"
				>
					<!-- Opaque tint + ink, not variant="tonal": tonal composites an
					alpha wash, and the design system rules tints opaque. -->
					<v-chip
						v-if="statusChip"
						size="x-small"
						variant="flat"
						:class="`bg-${statusChip.tint} text-${statusChip.ink}`"
						:prepend-icon="statusChip.icon"
					>
						{{ statusChip.label }}
					</v-chip>

					<!-- DCTL-2195: when this extraction was reassigned onto me from
					another curator, name that original assignee. Shown on rework and
					complete cards (a completed extraction taken over, or one reopened
					after being reassigned). Uses extraction_events.from_user, NOT
					reopened_user_id (which is whoever clicked reopen = me). -->
					<v-chip
						v-if="item.originalAssignee"
						size="x-small"
						variant="flat"
						prepend-icon="mdi-account-arrow-left-outline"
						class="text-truncate bg-valence-info-tint text-valence-info-ink"
						style="max-width: 180px"
						:title="'From ' + personLabel(item.originalAssignee)"
					>
						From {{ personLabel(item.originalAssignee) }}
					</v-chip>
				</div>

				<div v-if="people.length" class="mb-1">
					<div class="text-caption text-grey-darken-2 mb-1">
						{{ peopleLabel }}
					</div>
					<div class="d-flex flex-wrap ga-1">
						<v-chip
							v-for="(person, i) in people"
							:key="i"
							size="x-small"
							variant="flat"
							prepend-icon="mdi-account-outline"
							class="text-truncate bg-surface-tint text-fg-1"
							style="max-width: 160px"
						>
							{{ personLabel(person) }}
						</v-chip>
					</div>
				</div>

				<!-- Per-state actions. The row wraps so the always-present
				"View" secondary action (DCTL-2132) - and, on My Completed, the
				DCTL-2135 actions menu - can flow to a second line on the narrower
				cards (e.g. the three-button "assigned" state). -->
				<div class="mt-2 d-flex flex-wrap align-center ga-2">
					<v-btn
						v-if="state === 'available'"
						color="primary"
						size="small"
						variant="flat"
						class="flex-grow-1"
						:disabled="disabled"
						@click.stop="$emit('claim', item)"
					>
						<v-icon start size="small">mdi-hand-back-right</v-icon>
						Claim
					</v-btn>

					<template v-else-if="state === 'assigned'">
						<v-btn
							color="primary"
							size="small"
							variant="flat"
							class="flex-grow-1"
							:disabled="disabled"
							@click.stop="$emit('open', item)"
						>
							<v-icon start size="small">mdi-play</v-icon>
							Start
						</v-btn>
						<v-btn
							size="small"
							variant="outlined"
							:disabled="disabled"
							@click.stop="$emit('release', item)"
						>
							<v-icon start size="small">mdi-undo</v-icon>
							Release
						</v-btn>
					</template>

					<v-btn
						v-else-if="state === 'in_progress' || state === 'rework'"
						color="primary"
						size="small"
						variant="flat"
						class="flex-grow-1"
						:disabled="disabled"
						@click.stop="$emit('open', item)"
					>
						<v-icon start size="small">mdi-arrow-right</v-icon>
						Continue
					</v-btn>

					<!-- DCTL-2132: restore the old queue's read-only paper
					preview. Present on every state; secondary (white bg / blue
					text, non-outlined) so it never competes with the state's
					primary action. On My Completed it is the primary affordance
					(the download etc. moved into the actions menu below). -->
					<v-btn
						class="bg-white text-primary flex-grow-1"
						elevation="2"
						size="small"
						:disabled="disabled"
						@click.stop="$emit('view', item)"
					>
						<v-icon start size="small">mdi-file-eye-outline</v-icon>
						View
					</v-btn>

					<!-- DCTL-2135: the completed-extraction action set (download
					JSON / XLSX, tree view, history, reopen) on the "My Completed"
					card, keyed by my own completed data-entry (DCTL-2136). -->
					<completed-extraction-actions
						v-if="state === 'complete' && item.dataEntryUuid"
						:data-entry-uuid="item.dataEntryUuid"
						:paper-name="item.paperName"
						:disabled="disabled"
						@reopened="$emit('reopened', item)"
					/>
				</div>
			</v-card-text>
		</template>
	</kanban-card>
</template>

<script>
import { KanbanCard } from "@/components/kanban";
import CompletedExtractionActions from "@/components/CompletedExtractionActions";
import { WORKFLOW_STATES } from "@/constants/workflowStates";
import { useMainStore } from "@/store";
import { personName } from "@/utils/person";

// DCTL-2257: this board's own work-state ids (the API's `workState`, plus the
// column id for Available and Complete) mapped onto the shared workflow-state
// keys. Each board translates its wire vocabulary once, here, so the shared map
// stays canonical - see constants/workflowStates.js.
const STATE_KEYS = {
	available: "available",
	assigned: "assigned",
	in_progress: "progress",
	rework: "rework",
	complete: "complete",
};

export default {
	name: "SdeKanbanCard",

	components: {
		KanbanCard,
		CompletedExtractionActions,
	},

	props: {
		item: {
			type: Object,
			required: true,
		},
		columnId: {
			type: String,
			required: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},

	emits: ["claim", "release", "open", "view", "reopened"],

	computed: {
		// Unified per-card state. For the merged "My Papers" column this is the
		// paper's own work state (assigned / in_progress / rework); Available and
		// Complete cards fall back to their column id.
		state() {
			return this.item.workState || this.columnId;
		},

		// This card's key into the shared workflow-state map.
		stateKey() {
			return STATE_KEYS[this.state] || null;
		},

		// DCTL-2257: every state merged into "My Papers" now carries its own chip -
		// colour + glyph + label from the shared map. In-progress used to carry none
		// and be identified by its orange edge alone, which is exactly the WCAG
		// 1.4.1 problem (and identification by ABSENCE of a chip is not a channel a
		// user can read). Null for Available and Complete, which are the whole
		// content of their own column and are named by its header.
		statusChip() {
			if (this.columnId !== "my_papers") return null;
			return WORKFLOW_STATES[this.stateKey] || null;
		},

		// The current user's uuid, so "myself" can be dropped from the
		// co-assignee chips - this is "My Papers", so showing my own name is
		// redundant. Matches the public uuid the API serializes into assignees.
		currentUserUuid() {
			return useMainStore().user.uuid;
		},

		// Co-assignees shown on the card: the backend's `assignees` is the full
		// distinct-claimant set, so the current user is filtered out. DCTL-2136:
		// the "My Completed" card is my OWN completed extraction, so it uses the
		// same co-assignee list as the other mine states (no separate completer
		// list); Available cards show co-claimants too.
		people() {
			return (this.item.assignees || []).filter(
				(person) => person.uuid !== this.currentUserUuid,
			);
		},

		// Caption above the people chips, so a bare name is not ambiguous.
		peopleLabel() {
			return "Also assigned to";
		},
	},

	methods: {
		personLabel: personName,
	},
};
</script>
